import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
    useScannerCrop,
    useSetScannerCrop,
} from '@/services/scanner.service';
import {
    type Crop,
    croppedSize,
    hasCrop,
    imageDpi,
    NO_CROP,
    pxToMm,
    scannerCrop,
} from '@/lib/crop';
import { BASE } from '../../basePath';
import BackofficePage from '../../components/BackofficePage';
import ScannerCropViewer from './ScannerCropViewer';

const EDGES: { key: keyof Crop; label: string }[] = [
    { key: 'top', label: 'Top' },
    { key: 'bottom', label: 'Bottom' },
    { key: 'left', label: 'Left' },
    { key: 'right', label: 'Right' },
];

// What a scanner with no crop yet opens on: about what the frame measures on
// most devices, so setting one is a matter of adjusting rather than typing.
// Only a starting point — nothing is stored until it is saved.
const DEFAULT_CROP_MM = 15;
const STARTING_CROP: Crop = {
    top: DEFAULT_CROP_MM,
    bottom: DEFAULT_CROP_MM,
    left: DEFAULT_CROP_MM,
    right: DEFAULT_CROP_MM,
};

const sameCrop = (a: Crop, b: Crop) =>
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.right === b.right;

export default function BackOfficeScannerCrop() {
    const { serialNumber } = useParams();
    const { data, isLoading, error } = useScannerCrop(serialNumber);
    const setCrop = useSetScannerCrop();
    const saved = data ? scannerCrop(data) : NO_CROP;
    const [draft, setDraft] = useState<Crop>(NO_CROP);
    const [size, setSize] = useState<{ x: number; y: number } | null>(null);

    // Start from what is stored, and again whenever it is saved.
    useEffect(() => {
        if (!data) return;
        const stored = scannerCrop(data);
        setDraft(hasCrop(stored) ? stored : STARTING_CROP);
    }, [data]);

    const back = (
        <Link
            to={`${BASE}/backOffice/scanners`}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-xs text-[#a8b0bb] hover:bg-[#161c22] hover:text-white transition"
        >
            <ArrowLeftIcon className="h-3.5 w-3.5" />
            All scanners
        </Link>
    );

    const title = `Scanner ${serialNumber ?? ''}`;
    if (isLoading)
        return (
            <BackofficePage title={title} description="Loading…" actions={back}>
                <Skeleton className="w-full h-[600px] bg-[#1e242a]" />
            </BackofficePage>
        );
    if (error || !data)
        return (
            <BackofficePage title={title} description="Error" actions={back}>
                <div className="text-red-400 text-sm">{String(error)}</div>
            </BackofficePage>
        );

    const dirty = !sameCrop(draft, saved);
    // The image's size in millimetres, which is what the crop is set against.
    const dpi = data.image?.dpi ?? null;
    const sizeMm = size && {
        x: pxToMm(size.x, imageDpi(dpi, size.x)),
        y: pxToMm(size.y, imageDpi(dpi, size.x)),
    };
    const kept = sizeMm ? croppedSize(sizeMm.x, sizeMm.y, draft) : null;
    const tooLarge =
        !!sizeMm &&
        (draft.left + draft.right >= sizeMm.x ||
            draft.top + draft.bottom >= sizeMm.y);
    const sample = data.sample;

    return (
        <BackofficePage
            title={title}
            description={
                sample
                    ? `${data.projectName ?? '—'} · ${sample.name}`
                    : 'Not bound to a sample'
            }
            actions={back}
            wide
        >
            <div className="flex gap-6">
                <div className="flex-1 min-w-0 h-[calc(100vh-200px)]">
                    {data.imageUrl ? (
                        <ScannerCropViewer
                            imageUrl={data.imageUrl}
                            crop={draft}
                            dpi={dpi}
                            onSize={setSize}
                        />
                    ) : (
                        <div className="h-full flex items-center justify-center rounded-md ring-1 ring-[#1e242a] text-sm text-[#6b7480]">
                            {!sample
                                ? 'This scanner is not bound to a sample, so it has no images to set a crop on.'
                                : !data.image
                                  ? 'This scanner’s sample has no acquisitions yet.'
                                  : 'The first acquisition’s jp2 is not in S3.'}
                        </div>
                    )}
                </div>

                <aside className="w-72 flex-shrink-0 space-y-5">
                    {data.image && (
                        <div className="text-xs text-[#a8b0bb] space-y-1">
                            <div className="text-[10px] uppercase tracking-wider text-[#6b7480]">
                                First acquisition
                            </div>
                            <div>
                                {new Date(
                                    data.image.acquisitionDate,
                                ).toLocaleString()}
                                {data.image.dpi ? ` · ${data.image.dpi} dpi` : ''}
                            </div>
                        </div>
                    )}

                    <div className="space-y-3">
                        <div className="text-[10px] uppercase tracking-wider text-[#6b7480]">
                            Millimetres to crop, raw orientation
                        </div>
                        {EDGES.map(({ key, label }) => (
                            <label
                                key={key}
                                className="flex items-center justify-between gap-3 text-sm"
                            >
                                <span className="text-[#d8dde2]">{label}</span>
                                <input
                                    type="number"
                                    min={0}
                                    step={0.1}
                                    value={draft[key]}
                                    onChange={(e) => {
                                        // A hundredth of a millimetre is under
                                        // a pixel even at 1200 dpi.
                                        const value = Math.max(
                                            0,
                                            Math.round(
                                                (Number(e.target.value) || 0) * 100,
                                            ) / 100,
                                        );
                                        setDraft((d) => ({ ...d, [key]: value }));
                                    }}
                                    className="w-28 bg-[#0f141a] border border-[#1e242a] rounded-md px-3 py-1.5 text-sm font-mono text-right text-[#e5e7e3] focus:outline-none focus:border-[#2d6a4f]"
                                />
                            </label>
                        ))}
                        {kept && (
                            <div
                                className={`text-xs font-mono ${tooLarge ? 'text-red-400' : 'text-[#a8b0bb]'}`}
                            >
                                {tooLarge
                                    ? 'Crop is larger than the image'
                                    : `Kept: ${kept.width.toFixed(1)} × ${kept.height.toFixed(1)} mm`}
                            </div>
                        )}
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() =>
                                setCrop.mutate({
                                    serialNumber: data.serialNumber,
                                    crop: {
                                        cropTopMm: draft.top,
                                        cropBottomMm: draft.bottom,
                                        cropLeftMm: draft.left,
                                        cropRightMm: draft.right,
                                    },
                                })
                            }
                            disabled={!dirty || tooLarge || setCrop.isPending}
                            className="px-3 py-1.5 rounded-md text-xs font-medium border bg-[#1e2a22] border-[#2d6a4f] text-[#74c69d] hover:text-white disabled:opacity-40 disabled:pointer-events-none transition"
                        >
                            {setCrop.isPending ? 'Saving…' : 'Save'}
                        </button>
                        <button
                            onClick={() => setDraft(saved)}
                            disabled={!dirty}
                            className="px-3 py-1.5 rounded-md text-xs font-medium border bg-[#161c22] border-[#1e242a] text-[#a8b0bb] hover:text-white disabled:opacity-40 disabled:pointer-events-none transition"
                        >
                            Revert
                        </button>
                    </div>
                    {setCrop.isError && (
                        <div className="text-xs text-red-400">
                            Could not save the crop.
                        </div>
                    )}

                    {sample && (
                        <Link
                            to={`${BASE}/project/${sample.projectId}/${sample.id}`}
                            className="block text-xs text-[#74c69d] hover:underline"
                        >
                            Open the sample’s viewer →
                        </Link>
                    )}
                </aside>
            </div>
        </BackofficePage>
    );
}
